import React, { useEffect, useState } from "react";
import { Copy, Plus, X } from "lucide-react";
import PageHeader from "@/components/admin/PageHeader";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

export default function SettingsEmails() {
    const [data, setData] = useState(null);
    const [adding, setAdding] = useState(false);
    const [newEmail, setNewEmail] = useState("");

    useEffect(() => {
        adminApi.get("/api/admin/settings/emails").then((r) => setData(r.data));
    }, []);

    if (!data) return <div className="p-10 text-stone-500">Loading…</div>;

    const copy = (val) => {
        navigator.clipboard?.writeText(val);
        toast.success("Copied");
    };

    const addEmail = async () => {
        const v = newEmail.trim().toLowerCase();
        if (!v || !v.includes("@")) {
            toast.error("Enter a valid email");
            return;
        }
        const next = Array.from(new Set([...(data.custom_emails || []), v]));
        await adminApi.put("/api/admin/settings/emails", { custom_emails: next });
        setData({ ...data, custom_emails: next });
        setNewEmail("");
        setAdding(false);
        toast.success("Email address added");
    };

    const removeEmail = async (idx) => {
        const next = data.custom_emails.filter((_, i) => i !== idx);
        await adminApi.put("/api/admin/settings/emails", { custom_emails: next });
        setData({ ...data, custom_emails: next });
        toast.success("Email address removed");
    };

    return (
        <div className="p-10 max-w-4xl">
            <PageHeader eyebrow="Settings" title="Emails" description="The email address Priya replies from, and any custom addresses you've connected." testid="page-settings-emails" />

            <div className="bg-white border border-stone-200 rounded-lg p-6 mb-6">
                <h2 className="font-heading text-xl text-orchid-700 mb-1">Agent email address</h2>
                <p className="text-xs text-stone-500 mb-5">Automatic replies to guest enquiries are sent from this address.</p>

                <div className="flex items-center gap-2">
                    <input
                        readOnly
                        value={data.agent_email}
                        data-testid="agent-email-address"
                        className="flex-1 px-3 py-2 rounded-md border border-stone-200 text-sm bg-stone-50 text-stone-700 font-mono"
                    />
                    <button
                        onClick={() => copy(data.agent_email)}
                        data-testid="agent-email-copy"
                        className="p-2 rounded-md border border-stone-200 hover:bg-stone-50"
                    >
                        <Copy size={14} />
                    </button>
                </div>
            </div>

            <div className="bg-white border border-stone-200 rounded-lg p-6">
                <div className="flex items-start justify-between mb-5">
                    <div>
                        <h2 className="font-heading text-xl text-orchid-700 mb-1">Email configuration</h2>
                        <p className="text-xs text-stone-500">Use your own domain when sending replies — e.g. priya@orchidhotel.com.</p>
                    </div>
                    {!adding && (
                        <button
                            onClick={() => setAdding(true)}
                            data-testid="add-custom-email"
                            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md bg-orchid-700 hover:bg-orchid-600 text-white"
                        >
                            <Plus size={13} /> New email address
                        </button>
                    )}
                </div>

                {adding && (
                    <div className="flex items-center gap-2 mb-4 p-3 rounded-md bg-stone-50 border border-stone-200">
                        <input
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && addEmail()}
                            placeholder="priya@orchidhotel.com"
                            autoFocus
                            data-testid="custom-email-input"
                            className="flex-1 px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                        />
                        <button
                            onClick={addEmail}
                            data-testid="custom-email-save"
                            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md bg-orchid-700 hover:bg-orchid-600 text-white"
                        >
                            Add
                        </button>
                        <button
                            onClick={() => { setAdding(false); setNewEmail(""); }}
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-stone-200 hover:bg-stone-50"
                        >
                            Cancel
                        </button>
                    </div>
                )}

                {(data.custom_emails || []).length === 0 ? (
                    <div className="text-center py-10 text-sm text-stone-500">No custom email addresses configured.</div>
                ) : (
                    <ul className="divide-y divide-stone-100" data-testid="custom-emails-list">
                        {data.custom_emails.map((e, i) => (
                            <li key={i} className="py-3 flex items-center justify-between">
                                <span className="text-sm text-stone-700">{e}</span>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] uppercase tracking-wider text-sage-700">Verified</span>
                                    <button
                                        onClick={() => removeEmail(i)}
                                        data-testid={`custom-email-remove-${i}`}
                                        className="p-1 text-stone-400 hover:text-red-700"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
